import React from "react";
import {
  ArrowRight,
  Calendar,
  Check,
  Mic,
  Settings,
  Sparkles,
  Truck,
  Video,
} from "lucide-react";
import fieldCoachLogo from "@/assets/images/fieldcoach-logo.png";

const RESOURCE_URL =
  "https://hub.mycertainpath.com/hub/resources/fieldcoach-ai-virtual-ride-along-measurable-execution";

const HERO_STATS = [
  { value: "100%", icon: undefined, label: "Calls Captured" },
  { value: undefined, icon: Truck, label: "Ride-Along Scale" },
  { value: "Live", icon: undefined, label: "Conversation Scoring" },
];

// Illustrative product chrome for the hero mockup — not live data.
const SCRIPT_ROWS = [
  { label: "Initial Customer Greeting", hit: true, confidence: "High" },
  { label: "Service History", hit: true, confidence: "High" },
  { label: "Rapport Building", hit: false, confidence: "Missed" },
  { label: "Scope of Work", hit: true, confidence: "Medium" },
];

const SENTIMENT = [
  { score: "78", emoji: "🙂", label: "Overall" },
  { score: "83", emoji: "😊", label: "Employee" },
  { score: "73", emoji: "🙂", label: "Customer" },
];

const PHONE_TABS = [
  { icon: Calendar, label: "Calendar", active: false },
  { icon: Video, label: "Recordings", active: false },
  { icon: Mic, label: "Record", active: true },
  { icon: Settings, label: "Settings", active: false },
];

const DASH_TABS = ["Insights", "Metrics", "Script", "Coaching", "Summary"];

interface HeroBlockProps {
  /** Anchor the primary CTA scrolls to. */
  formSectionId: string;
}

const HeroBlock: React.FC<HeroBlockProps> = ({ formSectionId }) => (
  <section className="relative overflow-hidden bg-gradient-to-b from-cream to-white py-16 dark:from-slate-900 dark:to-slate-950 md:py-20">
    <style>{`
      @keyframes fc-rec-pulse {
        0%, 100% { box-shadow: 0 0 0 0 rgba(178,0,48,.5); }
        50% { box-shadow: 0 0 0 8px rgba(178,0,48,0); }
      }
    `}</style>

    <svg
      aria-hidden="true"
      className="pointer-events-none absolute right-[-50px] top-0 z-0 h-full w-[600px] opacity-55"
      fill="none"
      viewBox="0 0 600 800"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        className="stroke-gold"
        d="M 500 -50 C 450 150, 250 200, 320 400 C 380 580, 500 620, 460 850"
        fill="none"
        opacity=".7"
        strokeWidth="2"
      />
      <path
        className="stroke-primary"
        d="M 580 0 C 530 200, 330 250, 400 450 C 460 630, 580 670, 540 900"
        fill="none"
        opacity=".25"
        strokeWidth="2"
      />
    </svg>

    <div className="relative z-10 mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-6 lg:grid-cols-[1.05fr_0.95fr]">
      <div>
        <div className="text-[0.7rem] font-medium uppercase tracking-[0.18em] text-primary dark:text-primary-light">
          AI-Powered Virtual Ride-Along
        </div>

        <h1 className="mt-4 text-4xl font-light leading-[1.02] tracking-tight text-gray-900 dark:text-white md:text-5xl lg:text-6xl">
          You&rsquo;ve invested in great training.
          <br />
          <img
            alt="FieldCoach"
            className="mx-0.5 inline-block h-[1em] w-auto translate-y-[0.08em] align-baseline dark:brightness-0 dark:invert"
            src={fieldCoachLogo}
          />{" "}
          ensures your team uses it on every call.
        </h1>

        <p className="mt-7 max-w-xl text-lg font-light leading-relaxed text-gray-900 dark:text-gray-300">
          The only AI coaching platform built around the{" "}
          <strong className="font-extrabold text-primary dark:text-primary-light">
            CertainPath Operating System
          </strong>
          . Every field conversation becomes measurable, coachable, and
          accountable — aligned with your CertainPath scripts, processes, and
          standards.
        </p>

        <div className="mt-9 flex flex-wrap items-center gap-3.5">
          <a
            className="group inline-flex items-center justify-center gap-2 rounded-md bg-primary px-5 py-3 text-sm font-bold text-white transition-all duration-200 hover:-translate-y-px hover:bg-primary-dark dark:bg-primary-light dark:hover:bg-primary"
            href={`#${formSectionId}`}
          >
            Get Started with FieldCoach
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </a>
          <a
            className="inline-flex items-center justify-center gap-2 rounded-md border-[1.5px] border-secondary px-5 py-3 text-sm font-bold text-secondary transition-all duration-200 hover:bg-secondary hover:text-white dark:border-white dark:text-white dark:hover:bg-white dark:hover:text-secondary"
            href={RESOURCE_URL}
            rel="noopener noreferrer"
            target="_blank"
          >
            See how it works
          </a>
        </div>

        <div className="mt-12 flex flex-wrap gap-10 border-t border-gray-200 pt-7 dark:border-slate-700">
          {HERO_STATS.map((stat) => {
            const Icon = stat.icon;

            return (
              <div className="flex flex-col gap-1" key={stat.label}>
                <div className="text-[1.7rem] font-extrabold leading-none tracking-tight text-gray-900 dark:text-white">
                  {stat.value ?? (Icon ? <Icon className="h-7 w-7" /> : null)}
                </div>
                <div className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  {stat.label}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Layered product showcase */}
      <div
        aria-hidden="true"
        className="relative h-[480px] sm:h-[540px] lg:h-[600px]"
      >
        {/* Analytics dashboard — back layer */}
        <div className="absolute right-0 top-8 w-[75%] rotate-2 overflow-hidden rounded-2xl bg-[#0F1224] shadow-2xl">
          <div className="flex items-center justify-between border-b border-white/5 bg-[#0A0820] px-4 py-3">
            <div className="flex gap-1.5">
              <span className="h-2 w-2 rounded-full bg-[#FF5F57]" />
              <span className="h-2 w-2 rounded-full bg-[#FEBC2E]" />
              <span className="h-2 w-2 rounded-full bg-[#28C840]" />
            </div>
            <div className="rounded bg-white/5 px-2 py-1 text-[0.6rem] tracking-wide text-white/40">
              app.certainpath / recording / 7916
            </div>
          </div>

          <div className="flex flex-wrap gap-1.5 border-b border-white/5 px-4 pt-2.5">
            {DASH_TABS.map((tab) => (
              <div
                className={`rounded-t-md px-3 py-1.5 text-[0.7rem] font-medium ${
                  tab === "Metrics"
                    ? "border-b-2 border-gold bg-[#1A1734] text-gold"
                    : "text-white/50"
                }`}
                key={tab}
              >
                {tab}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-3 p-4">
            <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-[#1F1A40] to-[#2A1D52] p-3.5">
              <div className="text-[0.58rem] uppercase tracking-[0.14em] text-white/45">
                Recording
              </div>
              <div className="mt-1 text-xl font-extrabold leading-tight text-white">
                1h 5m 21s
              </div>
              <div className="mt-1.5 flex items-center gap-1.5 text-[0.62rem] font-light text-white/65">
                <span className="font-bold text-gold">68%</span>
                <span>Talk / 32% Listen</span>
              </div>
              <div className="mt-1.5 flex items-center gap-1.5 text-[0.62rem] font-light text-white/65">
                <span className="font-bold text-gold">43</span>
                <span>Words / minute</span>
              </div>
            </div>

            <div className="relative flex items-center justify-between overflow-hidden rounded-xl bg-gradient-to-br from-[#3D0E2A] to-[#5C1438] p-3.5">
              {SENTIMENT.map((item) => (
                <div
                  className="flex flex-col items-center gap-0.5"
                  key={item.label}
                >
                  <span className="text-xl font-extrabold text-white">
                    {item.score}
                    <span className="ml-0.5 text-[0.85rem]">{item.emoji}</span>
                  </span>
                  <span className="text-[0.55rem] uppercase tracking-wider text-white/60">
                    {item.label}
                  </span>
                </div>
              ))}
            </div>

            <div className="col-span-2 rounded-xl bg-[#1A1734] p-3.5">
              <div className="mb-2.5 flex items-center justify-between">
                <div className="text-[0.78rem] font-semibold text-white">
                  Comfort Experts · Service Call Script
                </div>
                <div className="rounded bg-gold/10 px-2 py-0.5 text-[0.65rem] font-semibold text-gold">
                  85% · 11/13
                </div>
              </div>

              {SCRIPT_ROWS.map((row) => (
                <div
                  className={`flex items-center gap-2 border-b border-white/5 py-1.5 text-[0.7rem] font-light last:border-b-0 ${
                    row.hit ? "text-white/85" : "text-white/40"
                  }`}
                  key={row.label}
                >
                  <div
                    className={`grid h-3.5 w-3.5 flex-shrink-0 place-items-center rounded-sm ${
                      row.hit ? "bg-primary" : "bg-white/10"
                    }`}
                  >
                    {row.hit && <Check className="h-2 w-2 text-white" />}
                  </div>
                  <span>{row.label}</span>
                  <span
                    className={`ml-auto rounded px-1.5 py-0.5 text-[0.55rem] font-semibold ${
                      row.confidence === "High"
                        ? "bg-[#779FA1]/20 text-[#779FA1]"
                        : "bg-gold/15 text-gold"
                    }`}
                  >
                    {row.confidence}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Phone — front layer */}
        <div className="absolute bottom-0 left-0 z-30 h-[410px] w-[200px] -rotate-[4deg] rounded-[2rem] bg-[#0B0820] p-2 shadow-[0_30px_70px_-25px_rgba(23,6,74,.32),0_0_0_4px_#1a1530,0_0_0_5px_rgba(255,255,255,.05)]">
          <div className="relative flex h-full w-full flex-col overflow-hidden rounded-3xl bg-[#0E0B22] px-3.5 py-4">
            <div className="absolute left-1/2 top-2 h-4 w-14 -translate-x-1/2 rounded-b-xl bg-black" />

            <div className="mb-5 mt-2 flex items-center justify-between px-1 text-[0.62rem] font-medium text-white/70">
              <span>4:37</span>
              <span className="flex items-center gap-1">
                <span className="h-1.5 w-1.5 rounded-full bg-white/70" />
                <span className="h-2 w-3 rounded-sm border border-white/70" />
              </span>
            </div>

            <div className="mb-auto border-b border-white/5 pb-3.5 text-center text-[0.82rem] font-medium text-white">
              Record
            </div>

            <div className="my-auto text-center">
              <div className="mb-1.5 text-[0.82rem] font-medium text-white">
                Bob &amp; Mary Smith
              </div>
              <div className="inline-flex items-center rounded-full bg-[#2A2548] px-2.5 py-1 text-[0.6rem] font-medium text-white">
                HVAC Service Call
              </div>
              <div className="mt-2 flex items-center justify-center gap-1 text-[0.62rem] text-white/50">
                <Calendar className="h-2.5 w-2.5" />
                3:00 PM
              </div>
            </div>

            <div className="mb-4 flex flex-col items-center">
              <div className="grid h-11 w-11 place-items-center rounded-full border-2 border-white bg-[#0E0B22]">
                <span
                  className="h-[18px] w-[18px] rounded-full bg-primary"
                  style={{
                    animation: "fc-rec-pulse 1.8s ease-in-out infinite",
                  }}
                />
              </div>
            </div>

            <div className="-mx-1 -mb-1 grid grid-cols-4 gap-0.5 border-t border-white/5 pt-2.5">
              {PHONE_TABS.map((tab) => {
                const Icon = tab.icon;

                return (
                  <div
                    className={`flex flex-col items-center gap-0.5 text-[0.5rem] font-medium ${
                      tab.active ? "text-gold" : "text-white/50"
                    }`}
                    key={tab.label}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    <span>{tab.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Floating AI coaching callout */}
        <div className="absolute left-[15%] top-[8%] z-40 flex max-w-[230px] -rotate-3 items-center gap-2.5 rounded-xl border border-gray-200 bg-white p-3 shadow-xl dark:border-slate-700 dark:bg-slate-800">
          <div className="grid h-8 w-8 flex-shrink-0 place-items-center rounded-lg bg-gradient-to-br from-secondary to-[#2A1A66] text-gold">
            <Sparkles className="h-4 w-4" />
          </div>
          <div className="text-[0.72rem] leading-tight">
            <strong className="block text-[0.74rem] font-bold text-gray-900 dark:text-white">
              AI Coaching Insight
            </strong>
            <span className="text-[0.65rem] font-light text-gray-500 dark:text-gray-400">
              Stronger fact-finding opportunity at 01:48
            </span>
          </div>
        </div>

        {/* Floating score badge */}
        <div className="absolute bottom-[15%] right-[8%] z-40 flex -rotate-2 items-center gap-3.5 rounded-2xl border border-gray-200 bg-white px-4 py-3.5 shadow-xl dark:border-slate-700 dark:bg-slate-800">
          <div className="relative grid h-12 w-12 place-items-center rounded-full bg-[conic-gradient(theme(colors.primary)_0%_87%,theme(colors.light)_87%_100%)] dark:bg-[conic-gradient(theme(colors.primary-light)_0%_87%,rgba(255,255,255,0.15)_87%_100%)]">
            <span className="absolute inset-1 rounded-full bg-white dark:bg-slate-800" />
            {/* Kept in sync by hand with the 87% stops in the ring above —
                Tailwind arbitrary values are build-time and cannot read a variable. */}
            <span className="relative text-[0.95rem] font-extrabold text-gray-900 dark:text-white">
              87
            </span>
          </div>
          <div className="flex flex-col">
            <span className="text-[0.58rem] uppercase tracking-widest text-gray-500 dark:text-gray-400">
              M. Torres
            </span>
            <span className="mt-0.5 text-[0.95rem] font-bold text-gray-900 dark:text-white">
              Script Score
            </span>
            <span className="mt-px text-[0.68rem] text-gray-500 dark:text-gray-400">
              +12% vs last week
            </span>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export default HeroBlock;
